/**
 * GuardianshipDetailDrawer — detalhe (GET /v1/guardianships/:id, #315) +
 * revogação (DELETE :id com reason + If-Match version). Reutiliza o Drawer do
 * DS (@lume/ui-components). Revogar só aparece quando status = active.
 *
 * Honest-defer visível: edição (PATCH scope/effectiveUntil/status) e
 * aprovação/co-signer Regulated.
 */

import { Drawer } from '@lume/ui-components';
import { useState } from 'react';

import { useGuardianship, useRevokeGuardianship } from '../../../hooks/use-guardianships';

import {
  GUARDIANSHIP_BASIS_LABEL,
  GUARDIANSHIP_SCOPE_FIELDS,
  GUARDIANSHIP_STATUS_LABEL,
  GUARDIANSHIP_TYPE_LABEL,
  type GuardianshipWireItem,
} from './types';

interface GuardianshipDetailDrawerProps {
  guardianshipId: string | null;
  onClose: () => void;
}

function fmt(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR', { dateStyle: 'medium' });
}

function DetailBody({ item }: { item: GuardianshipWireItem }): JSX.Element {
  const activeScope = GUARDIANSHIP_SCOPE_FIELDS.filter(({ key }) => item.scope[key] === true);

  return (
    <div className="g-detail">
      <div className="g-detail__row">
        <span className="g-detail__key">Tipo</span>
        <span className="g-detail__val">{GUARDIANSHIP_TYPE_LABEL[item.type].label}</span>
      </div>
      <div className="g-detail__row">
        <span className="g-detail__key">Status</span>
        <span className={`g-status g-status--${item.status}`}>
          {GUARDIANSHIP_STATUS_LABEL[item.status]}
        </span>
      </div>
      <div className="g-detail__row">
        <span className="g-detail__key">Ward</span>
        <span className="g-detail__val g-detail__val--mono">{item.wardId}</span>
      </div>
      <div className="g-detail__row">
        <span className="g-detail__key">Guardian</span>
        <span className="g-detail__val g-detail__val--mono">{item.guardianId}</span>
      </div>
      <div className="g-detail__row">
        <span className="g-detail__key">Base legal</span>
        <span className="g-detail__val">
          {item.basis !== null ? GUARDIANSHIP_BASIS_LABEL[item.basis] : '—'}
        </span>
      </div>
      <div className="g-detail__row">
        <span className="g-detail__key">Vigência</span>
        <span className="g-detail__val">
          {fmt(item.effectiveFrom)} → {fmt(item.effectiveUntil)}
        </span>
      </div>
      <div className="g-detail__row">
        <span className="g-detail__key">Documentação</span>
        <span className="g-detail__val">
          {item.documentReferenceId !== null ? (
            <span className="g-card__doc--ok">✓ {item.documentReferenceId}</span>
          ) : (
            <span className="g-card__doc--warn">⚠ sem documentação anexada</span>
          )}
        </span>
      </div>

      <div className="g-detail__scope">
        <span className="g-detail__key">Escopo</span>
        {activeScope.length > 0 ? (
          <ul className="g-detail__scope-list">
            {activeScope.map(({ key, label }) => (
              <li key={key}>✓ {label}</li>
            ))}
          </ul>
        ) : (
          <span className="g-detail__val">Não especificado</span>
        )}
      </div>

      {item.status === 'revoked' && (
        <div className="g-detail__row">
          <span className="g-detail__key">Revogada</span>
          <span className="g-detail__val">
            {fmt(item.revokedAt)} · {item.revokedReason ?? '—'}
          </span>
        </div>
      )}

      <p className="g-detail__defer">
        Edição de escopo/vigência e aprovação com co-signer (Regulated) são funções planejadas.
      </p>
    </div>
  );
}

export function GuardianshipDetailDrawer({
  guardianshipId,
  onClose,
}: GuardianshipDetailDrawerProps): JSX.Element {
  const { data: item, isLoading, isError } = useGuardianship(guardianshipId);
  const revoke = useRevokeGuardianship();

  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [revokeError, setRevokeError] = useState<string | null>(null);

  function reset(): void {
    setConfirming(false);
    setReason('');
    setRevokeError(null);
  }

  function handleClose(): void {
    reset();
    onClose();
  }

  function handleRevoke(): void {
    if (item === undefined || reason.trim().length < 5) {
      setRevokeError('Informe um motivo (mínimo 5 caracteres).');
      return;
    }
    setRevokeError(null);
    revoke.mutate(
      { id: item.id, version: item.version, reason: reason.trim() },
      {
        onSuccess: () => handleClose(),
        onError: (err) => setRevokeError(err.message !== '' ? err.message : 'Falha ao revogar.'),
      },
    );
  }

  const canRevoke = item?.status === 'active';

  const footer = canRevoke ? (
    confirming ? (
      <div className="g-scope g-revoke">
        <label className="g-field">
          <span className="g-field__label">Motivo da revogação</span>
          <textarea
            className="g-textarea"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="ex: cessação por decisão judicial / maioridade atingida"
          />
        </label>
        {revokeError !== null && (
          <p className="g-alert g-alert--error" role="alert">
            {revokeError}
          </p>
        )}
        <div className="g-revoke__actions">
          <button type="button" className="g-btn g-btn--ghost" onClick={reset}>
            Cancelar
          </button>
          <button
            type="button"
            className="g-btn g-btn--danger"
            disabled={revoke.isPending}
            onClick={handleRevoke}
          >
            {revoke.isPending ? 'Revogando…' : 'Confirmar revogação'}
          </button>
        </div>
      </div>
    ) : (
      <div className="g-scope">
        <button type="button" className="g-btn g-btn--danger" onClick={() => setConfirming(true)}>
          Revogar guardianship
        </button>
      </div>
    )
  ) : undefined;

  return (
    <Drawer
      isOpen={guardianshipId !== null}
      onClose={handleClose}
      title="Detalhe da guardianship"
      width="lg"
      footer={footer}
    >
      <div className="g-scope">
        {isLoading ? (
          <p className="g-muted">Carregando…</p>
        ) : isError || item === undefined ? (
          <p className="g-alert g-alert--error" role="alert">
            Falha ao carregar o detalhe.
          </p>
        ) : (
          <DetailBody item={item} />
        )}
      </div>
    </Drawer>
  );
}
